import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS } from "@store/theme/common/design";
import { checkoutBlockTitleStyles } from "@store/theme/common/mixins";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import React from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: OrderDetailsViewStyles;
}

export interface OrderDetailsViewStyles {
    container?: InjectableCss;
    shippingInfos?: InjectableCss;
    billingInfos?: InjectableCss;
    topContainer?: GridContainerProps;
    middleContainer?: GridContainerProps;
    bottomContainer?: GridContainerProps;
    orderTitleGridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    methodGridItem?: GridItemProps;
    totalInfosGridItem?: GridItemProps;
    linesGridItem?: GridItemProps;
    sectionTitle?: TypographyProps;
}

export const orderDetailsViewStyles: OrderDetailsViewStyles = {
    topContainer: {
        gap: 0,
        css: css`
            padding: 25px 0;
            border-top: 1px solid ${COLORS.border.base};
            border-bottom: 1px solid ${COLORS.border.base};
            margin-bottom: 25px;
        `,
    },
    middleContainer: {
        gap: 0,
    },
    bottomContainer: {
        gap: 0,
    },
    orderTitleGridItem: {
        width: [12, 12, 5, 6, 6],
    },
    buttonsGridItem: {
        width: [12, 12, 7, 6, 6],
        css: css`
            align-items: center;
        `,
    },
    methodGridItem: {
        width: [12, 12, 6, 8, 8],
        css: css`
            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["column", null, null, "row", null], properties: ["flex-direction"] }],
                    mediaOptions.min,
                )}
        `,
    },
    totalInfosGridItem: {
        width: [12, 12, 6, 4, 4],
    },
    linesGridItem: { width: 12 },
    sectionTitle: {
        variant: "h3",
        css: css`
            width: 100%;
            ${checkoutBlockTitleStyles()}
            margin-bottom: 25px;
        `,
    },
    shippingInfos: {
        css: css`
            width: 100%;
        `,
    },
    billingInfos: {
        css: css`
            width: 100%;
            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["20px 0", null, null, "0", null], properties: ["margin"] }],
                    mediaOptions.min,
                )}
        `,
    },
};

const StoreOrderDetailsView = ({ id, extendedStyles }: OwnProps) => {
    const [styles] = React.useState(() => mergeToNew(orderDetailsViewStyles, extendedStyles));
    return (
        <StyledWrapper {...styles.container}>
            <GridContainer {...styles.topContainer}>
                <GridItem {...styles.orderTitleGridItem}>
                    <Zone contentId={id} zoneName="Content0" />
                </GridItem>
                <GridItem {...styles.buttonsGridItem}>
                    <Zone contentId={id} zoneName="Content1" />
                </GridItem>
            </GridContainer>
            <GridContainer {...styles.middleContainer}>
                <GridItem {...styles.methodGridItem}>
                    <StyledWrapper {...styles.shippingInfos}>
                        <Typography {...styles.sectionTitle}>{translate("Shipping Information")}</Typography>
                        <Zone contentId={id} zoneName="Content2" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.billingInfos}>
                        <Typography {...styles.sectionTitle}>{translate("Billing Information")}</Typography>
                        <Zone contentId={id} zoneName="Content5" />
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.totalInfosGridItem}>
                    <Zone contentId={id} zoneName="Content3" />
                </GridItem>
            </GridContainer>
            <GridContainer {...styles.bottomContainer}>
                <GridItem {...styles.linesGridItem}>
                    <Zone contentId={id} zoneName="Content4" />
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const storeOrderDetailsView: WidgetModule = {
    component: StoreOrderDetailsView,
    definition: {
        displayName: "ViewStore",
        group: "Order Details",
        isSystem: true,
        allowedContexts: ["OrderDetailsPage"],
    },
};

export default storeOrderDetailsView;
